import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api/client.js';
import CustomerSearchSelect from '../components/CustomerSearchSelect.jsx';
import clsx from 'clsx';

const TOUCH_LABEL = { call: '📞 Called', email: '✉️ Emailed', text: '💬 Texted' };

export default function Blitz() {
  const qc = useQueryClient();

  const templates = useQuery({
    queryKey: ['note-templates'],
    queryFn: () => api.get('/note-templates').then(r => r.data.data),
  });
  const batches = useQuery({
    queryKey: ['blitz-batches'],
    queryFn: () => api.get('/blitz/batches').then(r => r.data.data),
  });

  const [templateId, setTemplateId] = useState('');
  const [showNewTemplate, setShowNewTemplate] = useState(false);

  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null); // { total_rows, matched, unmatched }
  const [resolutions, setResolutions] = useState({}); // row_index -> customer_id | 'skip'
  const [domain, setDomain] = useState('');

  const selectedTemplate = templates.data?.find(t => t.id === Number(templateId));

  const previewMutation = useMutation({
    mutationFn: (f) => {
      const fd = new FormData();
      fd.append('file', f);
      return api.post('/blitz/preview', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data.data);
    },
    onSuccess: (data) => {
      setPreview(data);
      setResolutions({});
    },
  });

  const confirmMutation = useMutation({
    mutationFn: (body) => api.post('/blitz/confirm', body).then(r => r.data.data),
    onSuccess: () => {
      setFile(null);
      qc.invalidateQueries({ queryKey: ['blitz-batches'] });
      // Deliberately NOT clearing preview/resolutions here -- the review
      // section (and its success message) lives inside {preview && ...},
      // so clearing it would hide the confirmation the moment it succeeds.
      // "Start a new blitz" below clears it explicitly instead.
    },
  });

  function startNewBlitz() {
    setFile(null);
    setPreview(null);
    setResolutions({});
    confirmMutation.reset();
  }

  const undoMutation = useMutation({
    mutationFn: (id) => api.post(`/blitz/batches/${id}/undo`).then(r => r.data.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['blitz-batches'] }),
  });

  const finalCustomerIds = preview
    ? [
        ...preview.matched.map(m => m.customer_id),
        ...Object.values(resolutions).filter(v => v && v !== 'skip').map(v => v.id),
      ]
    : [];

  function handleConfirm() {
    const body = { customer_ids: finalCustomerIds, domain: domain || undefined };
    if (templateId) body.template_id = templateId;
    else return; // save-first-or-write-adhoc guarded by disabled button below
    confirmMutation.mutate(body);
  }

  return (
    <div className="space-y-4 max-w-3xl">
      <header>
        <div className="section-eyebrow">Blitz</div>
        <h1 className="text-2xl">Log a customer blitz</h1>
        <div className="rule-crimson" />
        <p className="text-sm text-muted mt-2">
          Upload the customer list you already blitzed (by phone, email, or text) and log it against every
          customer at once, using a reusable template. This logs the outreach in the CRM — it does not send anything.
        </p>
      </header>

      {/* Template picker */}
      <section className="card p-4 space-y-3">
        <div className="section-eyebrow">1. Choose a template</div>
        <div className="rule-crimson" />
        <div className="flex gap-2 items-end flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <label className="label">Saved template</label>
            <select className="input" value={templateId} onChange={e => setTemplateId(e.target.value)}>
              <option value="">— Select a template —</option>
              {templates.data?.map(t => (
                <option key={t.id} value={t.id}>{t.name} ({TOUCH_LABEL[t.touch_type]?.split(' ')[1] || t.touch_type})</option>
              ))}
            </select>
          </div>
          <button className="btn-ghost text-sm" onClick={() => setShowNewTemplate(s => !s)}>
            {showNewTemplate ? 'Cancel' : '+ New template'}
          </button>
        </div>
        {selectedTemplate && (
          <div className="text-xs bg-panel rounded p-2 whitespace-pre-wrap text-muted">{selectedTemplate.body}</div>
        )}
        {showNewTemplate && (
          <NewTemplateForm
            onCreated={(t) => { setShowNewTemplate(false); setTemplateId(String(t.id)); qc.invalidateQueries({ queryKey: ['note-templates'] }); }}
          />
        )}
      </section>

      {/* Upload */}
      <section className="card p-4 space-y-3">
        <div className="section-eyebrow">2. Upload your customer list</div>
        <div className="rule-crimson" />
        <p className="text-xs text-muted">CSV with a customer name and/or account number column. Export it from whatever you already use to build the list.</p>
        <div className="flex gap-2 items-center flex-wrap">
          <input type="file" accept=".csv,text/csv" className="text-sm"
            onChange={e => setFile(e.target.files?.[0] || null)} />
          <button className="btn-primary text-sm" disabled={!file || previewMutation.isPending}
            onClick={() => previewMutation.mutate(file)}>
            {previewMutation.isPending ? 'Reading…' : 'Upload & match'}
          </button>
        </div>
        {previewMutation.isError && (
          <div className="text-crimson text-sm">{previewMutation.error?.response?.data?.error || 'Upload failed'}</div>
        )}
      </section>

      {/* Review */}
      {preview && (
        <section className="card p-4 space-y-4">
          <div className="section-eyebrow">3. Review matches</div>
          <div className="rule-crimson" />
          <p className="text-sm text-navy">
            {preview.matched.length} of {preview.total_rows} rows matched automatically.
            {preview.unmatched.length > 0 && ` ${preview.unmatched.length} need your input below.`}
          </p>

          {preview.matched.length > 0 && (
            <details open className="text-sm">
              <summary className="cursor-pointer text-navy font-medium">✓ Matched ({preview.matched.length})</summary>
              <div className="mt-2 space-y-1 max-h-48 overflow-y-auto">
                {preview.matched.map(m => (
                  <div key={m.row_index} className="flex justify-between text-xs py-1 px-2 rounded bg-emerald-50">
                    <span>{m.customer_name}</span>
                    <span className="text-muted">matched by {m.matched_by === 'account_number' ? 'account #' : 'name'}</span>
                  </div>
                ))}
              </div>
            </details>
          )}

          {preview.unmatched.length > 0 && (
            <div className="space-y-2">
              <div className="text-sm font-medium text-crimson">⚠ Needs your input ({preview.unmatched.length})</div>
              {preview.unmatched.map(u => (
                <UnmatchedRow key={u.row_index} row={u}
                  resolution={resolutions[u.row_index]}
                  onResolve={(v) => setResolutions(r => ({ ...r, [u.row_index]: v }))} />
              ))}
            </div>
          )}

          <div>
            <label className="label">Department (optional)</label>
            <select className="input max-w-xs" value={domain} onChange={e => setDomain(e.target.value)}>
              <option value="">— Unspecified —</option>
              <option value="parts">Parts</option>
              <option value="service">Service</option>
              <option value="trailer_sales">Trailer sales</option>
              <option value="other">Other</option>
            </select>
          </div>

          {!confirmMutation.isSuccess && (
            <div className="flex items-center gap-3 pt-2 border-t border-navy-100">
              <button className="btn-primary" disabled={!templateId || finalCustomerIds.length === 0 || confirmMutation.isPending}
                onClick={handleConfirm}>
                {confirmMutation.isPending ? 'Logging…' : `Log outreach for ${finalCustomerIds.length} customer${finalCustomerIds.length === 1 ? '' : 's'}`}
              </button>
              {!templateId && <span className="text-xs text-crimson">Choose a template above first</span>}
            </div>
          )}
          {confirmMutation.isError && (
            <div className="text-crimson text-sm">{confirmMutation.error?.response?.data?.error || 'Could not log outreach'}</div>
          )}
          {confirmMutation.isSuccess && (
            <div className="flex items-center gap-3 pt-2 border-t border-navy-100">
              <div className="text-emerald-700 text-sm font-medium">
                ✓ Logged {confirmMutation.data.note_count} note{confirmMutation.data.note_count === 1 ? '' : 's'}.
              </div>
              <button className="btn-ghost text-sm" onClick={startNewBlitz}>Start a new blitz</button>
            </div>
          )}
        </section>
      )}

      {/* Recent batches */}
      {batches.data?.length > 0 && (
        <section className="card p-4">
          <div className="section-eyebrow">Recent blitzes</div>
          <div className="rule-crimson" />
          <div className="mt-2 space-y-1">
            {batches.data.map(b => (
              <div key={b.id} className="flex items-center justify-between text-sm py-1.5 border-b border-navy-50 last:border-0">
                <div>
                  <span className={clsx(b.undone_at && 'line-through text-muted')}>
                    {b.template_name || 'One-off template'} — {b.customer_count} customer{b.customer_count === 1 ? '' : 's'}
                  </span>
                  <span className="text-muted ml-2 text-xs">{b.user_name} · {new Date(b.created_at).toLocaleString()}</span>
                </div>
                {!b.undone_at && (
                  <button className="btn-ghost text-xs px-2 py-1" disabled={undoMutation.isPending}
                    onClick={() => { if (confirm(`Undo this blitz? This deletes all ${b.customer_count} notes it created.`)) undoMutation.mutate(b.id); }}>
                    Undo
                  </button>
                )}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function UnmatchedRow({ row, resolution, onResolve }) {
  return (
    <div className="bg-amber-50 border border-amber-200 rounded p-2 text-sm space-y-1.5">
      <div className="text-navy">
        {row.raw.name || '(no name)'}
        {row.raw.account_number && <span className="text-muted"> · acct {row.raw.account_number}</span>}
      </div>
      {row.candidates.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {row.candidates.map(c => (
            <button key={c.id} type="button"
              className={clsx('text-xs px-2 py-1 rounded border',
                resolution?.id === c.id ? 'bg-navy text-white border-navy' : 'bg-white border-navy-200 hover:bg-panel')}
              onClick={() => onResolve({ id: c.id, name: c.name })}>
              {c.name}{c.city ? ` (${c.city})` : ''}
            </button>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <CustomerSearchSelect
            value={resolution && resolution !== 'skip' && !row.candidates.some(c => c.id === resolution.id) ? resolution.id : null}
            valueLabel={resolution?.name}
            allowHint={false}
            placeholder="Or search for the right customer…"
            onChange={(id, name) => onResolve({ id, name })}
          />
        </div>
        <button type="button"
          className={clsx('text-xs px-2 py-1 rounded border', resolution === 'skip' ? 'bg-crimson text-white border-crimson' : 'bg-white border-navy-200 hover:bg-panel')}
          onClick={() => onResolve('skip')}>
          Skip
        </button>
      </div>
    </div>
  );
}

function NewTemplateForm({ onCreated }) {
  const [name, setName] = useState('');
  const [body, setBody] = useState('');
  const [touchType, setTouchType] = useState('email');

  const create = useMutation({
    mutationFn: () => api.post('/note-templates', { name, body, touch_type: touchType }).then(r => r.data.data),
    onSuccess: (t) => onCreated(t),
  });

  return (
    <div className="border border-navy-100 rounded p-3 space-y-2">
      <div>
        <label className="label" htmlFor="blitz-template-name">Template name</label>
        <input id="blitz-template-name" className="input" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Fall parts promo" />
      </div>
      <div>
        <label className="label" htmlFor="blitz-template-type">Type</label>
        <select id="blitz-template-type" className="input" value={touchType} onChange={e => setTouchType(e.target.value)}>
          <option value="email">✉️ Email</option>
          <option value="call">📞 Call</option>
          <option value="text">💬 Text</option>
        </select>
      </div>
      <div>
        <label className="label" htmlFor="blitz-template-body">Message</label>
        <textarea id="blitz-template-body" className="input" rows={4} value={body} onChange={e => setBody(e.target.value)}
          placeholder="Use {{customer_name}} anywhere you want their name inserted." />
      </div>
      {create.isError && <div className="text-crimson text-xs">{create.error?.response?.data?.error || 'Could not save'}</div>}
      <button className="btn-primary text-sm" disabled={!name.trim() || !body.trim() || create.isPending}
        onClick={() => create.mutate()}>
        {create.isPending ? 'Saving…' : 'Save template'}
      </button>
    </div>
  );
}
